import i18n from "i18next";
import { ReactNode } from "react";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Dayjs } from "dayjs";
import { SalesLeadActivitiesApi } from "src/api/generated/erp/sales/crm/salesLeadActivities/api/salesLeadActivitiesApi.ts";
import { SalesLeadActivityView } from "src/api/generated/erp/db/types/tables/salesLeadActivityView.ts";
import { getSalesLeadActivityPriorityLabels } from "src/api/generated/erp/db/types/enums/salesLeadActivityPriority.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { dateTimeColumn, enumColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel";
import { AavoDatePicker } from "src/components/common/inputFields/AavoDatePicker.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import {
	SALES_LEAD_ACTIVITY_FORM_DIALOG_SIZE,
	SALES_LEAD_ACTIVITY_FORM_DIALOG_TITLE,
} from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivityFormDialog.utils.ts";
import { SalesLeadActivityForm } from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivityForm.tsx";
import {
	SalesLeadActivityCompletionMenuItems,
	SalesLeadActivitiesFilterMenuItems,
} from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivitiesMenuItems.tsx";
import {
	getSalesLeadActivityDueDateFilterApiParams,
	SalesLeadActivityDueDateFilter,
	salesLeadActivityDueDateFilters,
} from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivitiesDataGridTimeFilters.ts";
import { formatSalesLeadActivityDue } from "src/components/views/erp/sales/crm/salesLeadActivities/salesLeadActivityDue.ts";
import { salesLeadActivitiesToggleButtonSx } from "src/components/views/erp/sales/crm/salesLeadActivities/salesLeadActivitiesToggleStyles.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesLeadActivitiesDataGridFetchParams {
	searchQuery: string;
	salesLeadId?: number;
	showOpen: boolean;
	showCompleted: boolean;
	dueDateFilter: SalesLeadActivityDueDateFilter;
	customStartDate: Dayjs | null;
	customEndDate: Dayjs | null;
}

export interface SalesLeadActivitiesDataGridProps {
	salesLeadId?: number;
	refreshRef?: RefreshableElementRef<SalesLeadActivitiesDataGridFetchParams>;
	viewModeToggle?: ReactNode;
	/** Called after activities are added, edited, completed or deleted from this grid. */
	onActivitiesChanged?: () => Promise<unknown> | void;
}

export const SalesLeadActivitiesDataGrid = ({
	salesLeadId,
	refreshRef,
	viewModeToggle,
	onActivitiesChanged,
}: SalesLeadActivitiesDataGridProps) => {
	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.SalesActivities.MANAGE);
	// Lead-level columns repeat the same values on every row when the grid shows a single lead's activities.
	const showLeadColumns = salesLeadId == null;
	return (
		<ServerSideDataGridModel
			customFunctionUiActionView={"SALES_LEAD_ACTIVITIES"}
			fetchData={({ dueDateFilter, customStartDate, customEndDate, ...params }) =>
				SalesLeadActivitiesApi.getActivities({
					...params,
					...getSalesLeadActivityDueDateFilterApiParams({
						dueDateFilter,
						customStartDate,
						customEndDate,
					}),
				})
			}
			getDataModelResult={(response) => response}
			initialParams={{
				searchQuery: "",
				salesLeadId,
				showOpen: true,
				showCompleted: false,
				dueDateFilter: "all",
				customStartDate: null,
				customEndDate: null,
			}}
			getRowId={(row) => row.salesLeadActivityId}
			gridId={"F4C2B7A0E5D9431B"}
			refreshRef={refreshRef}
			render={({ dataGridProps, refreshData, currentParams }) => {
				const overdueSelected = currentParams.dueDateFilter === "overdue";
				const refreshActivities = async () => {
					await Promise.all([refreshData(), onActivitiesChanged?.()]);
				};

				return (
					<FormCrudDataGrid<SalesLeadActivityView>
						actionBarComponents={
							<SalesLeadActivitiesDataGridActionBar
								currentParams={currentParams}
								refreshData={refreshData}
								viewModeToggle={viewModeToggle}
							/>
						}
						actionBarMenuComponents={
							<SalesLeadActivitiesFilterMenuItems
								showOpen={overdueSelected ? true : currentParams.showOpen}
								showCompleted={overdueSelected ? false : currentParams.showCompleted}
								disabled={overdueSelected}
								onShowOpenChange={(showOpen) => {
									if (!overdueSelected) refreshData({ showOpen });
								}}
								onShowCompletedChange={(showCompleted) => {
									if (!overdueSelected) refreshData({ showCompleted });
								}}
							/>
						}
						rowContextMenuComponents={
							editable ?
								({ allSelectedRows }) => (
									<SalesLeadActivityCompletionMenuItems
										activities={allSelectedRows}
										onCompleted={refreshActivities}
									/>
								)
							:	undefined
						}
						columns={[
							textColumn({
								field: "title",
								headerName: i18n.t("title"),
								width: 220,
							}),
							textColumn({
								field: "description",
								headerName: i18n.t("description"),
								width: 280,
							}),
							showLeadColumns &&
								textColumn({
									field: "salesLeadDescription",
									headerName: i18n.t("sales_lead_description"),
									width: 280,
								}),
							showLeadColumns &&
								textColumn({
									field: "salesLeadCustomerName",
									headerName: i18n.t("customer_name"),
									width: 220,
								}),
							textColumn({
								field: "salespersonName",
								headerName: i18n.t("salesperson"),
								width: 180,
							}),
							dateTimeColumn({
								field: "dueAt",
								headerName: i18n.t("deadline"),
								width: 150,
								valueFormatter: (_, row) => formatSalesLeadActivityDue(row),
							}),
							enumColumn({
								field: "priority",
								headerName: i18n.t("priority"),
								width: 120,
								enumLabels: getSalesLeadActivityPriorityLabels(),
							}),
							textColumn({
								field: "salesLeadActivityCategoryName",
								headerName: i18n.t("category"),
								width: 160,
							}),
							showLeadColumns &&
								textColumn({
									field: "salesLeadContactEmail",
									headerName: i18n.t("email"),
									width: 220,
								}),
							showLeadColumns &&
								textColumn({
									field: "salesLeadContactPhone",
									headerName: i18n.t("phone"),
									width: 160,
								}),
							dateTimeColumn({
								field: "completedAt",
								headerName: i18n.t("completed_at"),
								width: 180,
							}),
							dateTimeColumn({
								field: "lastUpdated",
								headerName: i18n.t("update_time"),
								width: 180,
							}),
						]}
						form={{
							addRowEnabled: editable,
							editEnabled: editable,
							dialogSize: SALES_LEAD_ACTIVITY_FORM_DIALOG_SIZE,
							dialogTitle: SALES_LEAD_ACTIVITY_FORM_DIALOG_TITLE,
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.sales.crm.salesLeadActivity.formDialog"),
								data: {
									salesLeadActivityId: row?.salesLeadActivityId ?? null,
									salesLeadId: row?.salesLeadId ?? salesLeadId ?? null,
								},
								pathSegments:
									row?.salesLeadActivityId ?
										[`${i18n.t("activity")} ${row.salesLeadActivityId}`]
									:	[i18n.t("activity")],
							}),
							component: ({ row, onCompleted, onFormEdited }) => (
								<SalesLeadActivityForm
									salesLeadActivityId={row?.salesLeadActivityId}
									initialSalesLeadId={salesLeadId}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
								/>
							),
						}}
						remove={
							editable ?
								async ({ items }) => {
									await SalesLeadActivitiesApi.delete_({
										salesLeadActivityIds: items.map((item) => item.salesLeadActivityId),
									});
								}
							:	undefined
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.crm.salesLeadActivities.grid"),
							data: { salesLeadId: salesLeadId ?? null },
							selectedRowPathSegment: (row) => `${i18n.t("activity")} ${row.salesLeadActivityId}`,
							rowFields: ["salesLeadActivityId", "salesLeadId", "title", "dueAt", "allDay", "priority"],
						}}
						{...dataGridProps}
						refreshData={refreshActivities}
					/>
				);
			}}
		/>
	);
};

interface SalesLeadActivitiesDataGridActionBarProps {
	currentParams: SalesLeadActivitiesDataGridFetchParams;
	refreshData: (params: Partial<SalesLeadActivitiesDataGridFetchParams>) => Promise<unknown> | void;
	viewModeToggle?: ReactNode;
}

const SalesLeadActivitiesDataGridActionBar = ({
	currentParams,
	refreshData,
	viewModeToggle,
}: SalesLeadActivitiesDataGridActionBarProps) => {
	return (
		<>
			<AavoTextField label={i18n.t("search")} onSubmit={(searchQuery) => refreshData({ searchQuery })} />
			<ToggleButtonGroup
				value={currentParams.dueDateFilter}
				exclusive
				size="small"
				color="primary"
				onChange={(_, dueDateFilter: SalesLeadActivityDueDateFilter | null) => {
					if (dueDateFilter == null) return;
					refreshData({
						dueDateFilter,
						...(dueDateFilter === "overdue" && {
							showOpen: true,
							showCompleted: false,
						}),
					});
				}}
			>
				{salesLeadActivityDueDateFilters.map((filter) => (
					<ToggleButton
						key={filter}
						value={filter}
						aria-label={getSalesLeadActivityDueDateFilterLabel(filter)}
						sx={salesLeadActivitiesToggleButtonSx}
					>
						{getSalesLeadActivityDueDateFilterLabel(filter)}
					</ToggleButton>
				))}
			</ToggleButtonGroup>
			{currentParams.dueDateFilter === "custom" && (
				<>
					<AavoDatePicker
						label={i18n.t("from_date")}
						value={currentParams.customStartDate}
						onChange={(customStartDate) => refreshData({ customStartDate })}
						sx={{ width: 150 }}
					/>
					<AavoDatePicker
						label={i18n.t("to_date")}
						value={currentParams.customEndDate}
						onChange={(customEndDate) => refreshData({ customEndDate })}
						sx={{ width: 150 }}
					/>
				</>
			)}
			{viewModeToggle != null && (
				<Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>{viewModeToggle}</Box>
			)}
		</>
	);
};

function getSalesLeadActivityDueDateFilterLabel(filter: SalesLeadActivityDueDateFilter): string {
	switch (filter) {
		case "all":
			return i18n.t("sales_lead_activity_due_date_filter_all");
		case "overdue":
			return i18n.t("sales_lead_activity_due_date_filter_overdue");
		case "today":
			return i18n.t("sales_lead_activity_due_date_filter_today");
		case "tomorrow":
			return i18n.t("sales_lead_activity_due_date_filter_tomorrow");
		case "thisWeek":
			return i18n.t("sales_lead_activity_due_date_filter_this_week");
		case "nextWeek":
			return i18n.t("sales_lead_activity_due_date_filter_next_week");
		case "custom":
			return i18n.t("sales_lead_activity_due_date_filter_custom");
	}
}
